import { useCallback, useEffect, useState } from "react";
import { PaymentTypes } from "../../api/types/order.types";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import { formatDate } from "../../utils/date";
import styles from "./Orders.module.scss";
import OrderService from "../../api/services/orders.service";
import Loading from "../../components/common/loading/Loading";
import { useTranslation } from "react-i18next";

const StatusButton = ({ status }: { status: string }) => {
  const { t } = useTranslation("translation");
  switch (status) {
    case "1":
      return (
        <div className={styles.confirmBtn}>
          {t("payments.status.not_completed")}
        </div>
      );
    case "2":
      return (
        <div className={styles.readyBtn}>{t("payments.status.success")}</div>
      );
    case "3":
      return (
        <div className={styles.adminBtn}>{t("payments.status.failed")}</div>
      );
    default:
      return (
        <div className={styles.unknownBtn}>{t("payments.status.unknown")}</div>
      );
  }
};

const Orders = () => {
  const [count, setCount] = useState<number | null>(null);
  const [paymnets, setPayments] = useState<PaymentTypes[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("translation");

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OrderService.getAllPayments(page, limit);
      setPayments(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const columns: Column[] = [
    {
      key: "order_id",
      header: t("columns.order_id") || "Order ID",
      render: (v) => <p className={styles.box}>{v}</p>,
    },
    {
      key: "transaction_id",
      header: t("columns.transaction_id") || "Transaction ID",
      render: (v) => <p className={styles.content}>{v}</p>,
    },
    {
      key: "user",
      header: t("columns.client") || "Client",
      render: (_, row) => (
        <p className={styles.content}>
          {row?.user?.first_name} {row?.user?.last_name}
        </p>
      ),
    },
    {
      key: "amount",
      header: t("columns.amount") || "Amount",
      render: (_, row) => (
        <p>
          {row.amount}
          {row.currency_name}
        </p>
      ),
    },
    {
      key: "card_number",
      header: t("columns.card_number") || "Card Number",
    },
    {
      key: "created",
      header: t("columns.date") || "Date",
      render: (v) => formatDate(v),
    },
    {
      key: "status",
      header: t("columns.status") || "Status",
      render: (value) => {
        return <StatusButton status={value} />;
      },
    },
  ];

  if (loading) return <Loading />;

  return (
    <div className={styles.orders}>
      <TitleUpdater title={t("orders")} />
      <div className={styles.section_header}>
        <h1 className={styles.title}>{t("orders")}</h1>
      </div>
      <DataTable
        data={paymnets}
        columns={columns}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      />
    </div>
  );
};

export default Orders;
